import { CollectorManagementPage } from '@/components/features/collectors/CollectorManagementPage'
import { VoucherType } from '@/domain/invoices/invoice'
import { AccountsCnt } from '@/pages/accounts/AccountsCnt'
import { CompleteRedemptionPage } from '@/pages/agreements/CompleteRedemptionPage'
import { ContractsCnt } from '@/pages/agreements/ContractsCnt'
import { CremationsManagement } from '@/pages/agreements/CremationsManagement'
import { PaymentPlansManagement } from '@/pages/agreements/PaymentPlansManagement'
import { SubscriptionsManagement } from '@/pages/agreements/SubscriptionsManagement'
import { ClientsCnt } from '@/pages/clients/ClientsCnt'
import { HomePage } from '@/pages/homepage/HomePage'
import { EditInvoiceItems } from '@/pages/invoices/EditInvoiceItems'
import { InvoiceGenerationStatus } from '@/pages/invoices/InvoiceGenerationStatus'
import { InvoicePage } from '@/pages/invoices/InvoicePage'
import { AddingAdditionalParcelPage } from '@/pages/parcels/AddingAdditionalParcelPage'
import { OccupyingParcelPage } from '@/pages/parcels/OccupyingParcelPage'
import { ParcelContractPage } from '@/pages/parcels/ParcelContractPage'
import { ParcelsCnt } from '@/pages/parcels/ParcelsCnt'
import { PaymentsCnt } from '@/pages/payments/PaymentsCnt'
import { SubscriptionPaymentsCnt } from '@/pages/payments/SubscriptionPaymentsCnt'
import { ReportsCnt } from '@/pages/reports/ReportsCnt'
import { SalesCnt } from '@/pages/sales/SalesCnt'
import UnderConstructionPage from '@/pages/under-construction'
import { UsersCnt } from '@/pages/users/UsersCnt'
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'
import { z } from 'zod'
import { ProtectedRoute } from '../components/features/shared/ProtectedRoute'
import { Layout } from '../components/layouts/main/Layout'
import { UserRole } from '../domain/auth/auth'
import { ManagementLogin } from '../pages/auth/ManagementLogin'
import { OfferingsCnt } from '../pages/offerings/OfferingsCnt'
import { getRelativePath, Paths } from './paths'

const parcelSearchSchema = z.object({
  parcelId: z.string().optional(),
})

const agreementsSearchSchema = z.object({
  page: z.number().optional().catch(1),
  search: z.string().optional().catch(''),
  status: z.string().optional().catch('all'),
})

const collectorsSearchSchema = z.object({
  collectorId: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  page: z.coerce.number().optional().catch(0),
  pageSize: z.coerce.number().optional().catch(50),
  status: z.string().optional().default('all'),
  serviceType: z.string().optional().default('all'),
  offeringId: z.string().optional(),
})

const invoicesSearchSchema = z.object({
  search: z.string().optional().catch(''),
  status: z.string().optional().catch('all'),
  voucherType: z.string().optional().catch('all'),
  fromDate: z.string().optional().catch(''),
  toDate: z.string().optional().catch(''),
  page: z.coerce.number().optional().catch(1),
  limit: z.coerce.number().optional().catch(20),
  pageSize: z.coerce.number().optional().catch(10),
})

const accountDetailsSearchSchema = z.object({
  page: z.number().optional().catch(1),
  limit: z.number().optional().catch(20),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  accountId: z.string().optional(),
})

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const managementLoginRoutes = [
  { path: Paths.PublicLogin },
  { path: Paths.ManagementLogin },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: routeConfig.path,
    component: () => (
      <ManagementLogin />
    ),
  })
)

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Paths.Management.root,
  component: Layout,
})

const managementDashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Dashboard),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <HomePage />
    </ProtectedRoute>
  ),
})

const clientsRoutes = [
  { path: getRelativePath(Paths.Management.Clients) },
  { path: getRelativePath(Paths.Management.Clients) + '/$clientId' },
  { path: getRelativePath(Paths.Management.NewClient) },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <ClientsCnt />
      </ProtectedRoute>
    ),
  })
)

const contractRoutes = [
  { path: getRelativePath(Paths.Management.Contracts) },
  { path: getRelativePath(Paths.Management.Contracts) + '/$contractId' },
  { path: getRelativePath(Paths.Management.NewContract) },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    validateSearch: agreementsSearchSchema,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <ContractsCnt />
      </ProtectedRoute>
    ),
  })
)

const paymentRoutes = [
  { path: getRelativePath(Paths.Management.Contracts) + `/$contractId/${Paths.Management.Payments}/$paymentId` },
  { path: `/${getRelativePath(Paths.Management.MultiPayments)}/$paymentId` },
  { path: getRelativePath(Paths.Management.PaymentInvoices) + `/$paymentId` },
  { path: getRelativePath(Paths.Management.CollectorInvoices) + `/$collectorId` }
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin]}>
        <PaymentsCnt />
      </ProtectedRoute>
    ),
  })
)

const subscriptionPaymentRoutes = [
  { path: getRelativePath(Paths.Management.SubscriptionPeriodPayments) + `$billingPeriodId/${Paths.Management.Payments}/$paymentId` },
  { path: getRelativePath(Paths.Management.SubscriptionPayments) + `/$subscriptionId/${Paths.Management.Payments}/$paymentId` },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <SubscriptionPaymentsCnt />
      </ProtectedRoute>
    ),
  })
)

const accountsRoutes = [
  { path: getRelativePath(Paths.Management.Accounts) },
  { path: getRelativePath(Paths.Management.CashBox) },
  { path: getRelativePath(Paths.Management.CashBoxMovements) },
  { path: getRelativePath(Paths.Management.AccountDetails) },
  { path: getRelativePath(Paths.Management.MainAccounts) },
  { path: getRelativePath(Paths.Management.CollectorAccounts) },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    validateSearch: accountDetailsSearchSchema,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <AccountsCnt />
      </ProtectedRoute>
    ),
  })
)

const subscriptionsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Subscriptions),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <SubscriptionsManagement />
    </ProtectedRoute>
  ),
})

const paymentPlansRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.PaymentPlans),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <PaymentPlansManagement />
    </ProtectedRoute>
  ),
})

const cremationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Cremations),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <CremationsManagement />
    </ProtectedRoute>
  ),
})

const parcelsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Parcels),
  validateSearch: parcelSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <ParcelsCnt />
    </ProtectedRoute>
  ),
})

const parcelsSellRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.ParcelsSell),
  validateSearch: parcelSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <ParcelsCnt />
    </ProtectedRoute>
  ),
})

const parcelContractRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.ParcelContract),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <ParcelContractPage />
    </ProtectedRoute>
  ),
})

const occupyingParcelRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.OccupyingParcel),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <OccupyingParcelPage />
    </ProtectedRoute>
  ),
})

const addingAdditionalParcelRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.AddingAdditionalParcel),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <AddingAdditionalParcelPage />
    </ProtectedRoute>
  ),
})

const completeRedemptionRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.CompleteRedemption),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <CompleteRedemptionPage />
    </ProtectedRoute>
  ),
})

const invoicingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Invoicing),
  validateSearch: invoicesSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <InvoicePage />
    </ProtectedRoute>
  ),
})

const invoiceGenerationStatusSearchSchema = z.object({
  voucherType: z.enum(VoucherType).optional().catch(undefined),
  jobId: z.string().optional().catch(''),
})

const invoiceGenerationStatusRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.InvoiceGenerationStatus + '/$invoiceId'),
  validateSearch: invoiceGenerationStatusSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <InvoiceGenerationStatus />
    </ProtectedRoute>
  ),
})

const editInvoiceItemsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.EditInvoiceItems + '/$invoiceId'),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <EditInvoiceItems />
    </ProtectedRoute>
  ),
})

const offeringRoutes = [
  { path: getRelativePath(Paths.Management.NewOfferingPrice) },
  { path: getRelativePath(Paths.Management.EditOffering) + '/$offeringId' },
  { path: getRelativePath(Paths.Management.Offerings) + '/$offeringId' },
  { path: getRelativePath(Paths.Management.NewOffering) },
  { path: getRelativePath(Paths.Management.Offerings) },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <OfferingsCnt />
      </ProtectedRoute>
    ),
  })
)


const reportsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Reports),
  validateSearch: accountDetailsSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.SuperAdmin, UserRole.Scheduler]}>
      <ReportsCnt />
    </ProtectedRoute>
  ),
})

const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Settings),
  component: () => (
    <ProtectedRoute requireManagementAccess>
      <UnderConstructionPage />
    </ProtectedRoute>
  ),
})

const collectorsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Collectors),
  validateSearch: collectorsSearchSchema,
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <CollectorManagementPage />
    </ProtectedRoute>
  ),
})

const usersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: getRelativePath(Paths.Management.Users),
  component: () => (
    <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
      <UsersCnt />
    </ProtectedRoute>
  ),
})

const salesRoutes = [
  { path: getRelativePath(Paths.Management.Sales) },
  { path: getRelativePath(Paths.Management.SellerSales) },
  { path: getRelativePath(Paths.Management.OfferingSales) },
].map((routeConfig) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path: routeConfig.path,
    validateSearch: z.object({
      periodStart: z.string().optional(),
      periodEnd: z.string().optional(),
      viewMode: z.enum(['sellers', 'offerings']).optional().default('sellers'),
      sellerId: z.string().optional().default('all'),
      serviceType: z.string().optional().default('all'),
      offeringId: z.string().optional().default('all'),
      page: z.coerce.number().optional().catch(1),
    }),
    component: () => (
      <ProtectedRoute requiredRoles={[UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]}>
        <SalesCnt />
      </ProtectedRoute>
    ),
  })
)

const routeTree = rootRoute.addChildren([
  ...managementLoginRoutes,
  layoutRoute.addChildren([
    managementDashboardRoute,
    ...clientsRoutes,
    ...contractRoutes,
    ...paymentRoutes,
    ...subscriptionPaymentRoutes,
    ...offeringRoutes,
    ...accountsRoutes,
    ...salesRoutes,
    cremationsRoute,
    subscriptionsRoute,
    paymentPlansRoute,
    parcelsRoute,
    parcelsSellRoute,
    parcelContractRoute,
    occupyingParcelRoute,
    addingAdditionalParcelRoute,
    usersRoute,
    collectorsRoute,
    completeRedemptionRoute,
    invoicingRoute,
    invoiceGenerationStatusRoute,
    editInvoiceItemsRoute,
    reportsRoute,
    settingsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
